import { useEffect, useState, type FormEvent } from 'react'
import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react'
import {
  hasVerifiedTotpFactor,
  startTotpEnrolment,
  verifyTotpCode,
  type TotpEnrolment,
} from '@/auth/mfa'
import { Button } from '@/components/ui/button'
import { MfaCodeInput } from '@/features/mfa/MfaCodeInput'

type Phase = 'loading' | 'already' | 'scan' | 'done'

/**
 * TOTP enrolment flow (spec-14b Phase 2). Calls `mfa.enroll`, renders the QR
 * code + text secret, then verifies the first 6-digit code (challenge + verify)
 * so enrolment finishes *verified* — which is what stamps the session to aal2.
 *
 * Already-enrolled accounts are detected up-front (listFactors) and never
 * double-enrol. Inlined so it can host on the Security page or in a dialog.
 * Admin/team only — gated by the caller; founders never see this.
 */
export function MfaEnrolment({ onVerified }: { onVerified?: () => void }) {
  const [phase, setPhase] = useState<Phase>('loading')
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // On mount, skip straight to "already enrolled" when a verified factor exists,
  // otherwise begin a fresh enrolment to fetch the QR/secret.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        if (await hasVerifiedTotpFactor()) {
          if (!cancelled) setPhase('already')
          return
        }
        const next = await startTotpEnrolment()
        if (!cancelled) {
          setEnrolment(next)
          setPhase('scan')
        }
      } catch {
        if (!cancelled) {
          setError('Inschrijven kon niet worden gestart. Probeer het later opnieuw.')
          setPhase('scan')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  async function handleVerify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!enrolment) return
    setSubmitting(true)
    setError(null)
    try {
      await verifyTotpCode(enrolment.factorId, code)
      setPhase('done')
      onVerified?.()
    } catch {
      setError('Onjuiste of verlopen code. Probeer het opnieuw.')
    } finally {
      setSubmitting(false)
    }
  }

  if (phase === 'loading') {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        Bezig met laden…
      </p>
    )
  }

  if (phase === 'already') {
    return (
      <div className="flex items-start gap-3 rounded-lg bg-muted/50 p-4">
        <ShieldCheck className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
        <div className="flex flex-col gap-1">
          <p className="font-medium text-foreground">
            Tweestapsverificatie is al ingesteld
          </p>
          <p className="text-sm text-muted-foreground">
            Je account is beveiligd met een authenticator-app. Bij de volgende keer
            inloggen vragen we om een code.
          </p>
        </div>
      </div>
    )
  }

  if (phase === 'done') {
    return (
      <div
        role="status"
        className="flex items-start gap-3 rounded-lg bg-muted/50 p-4"
      >
        <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
        <div className="flex flex-col gap-1">
          <p className="font-medium text-foreground">Tweestapsverificatie is actief</p>
          <p className="text-sm text-muted-foreground">
            Je sessie is verhoogd naar het hoogste beveiligingsniveau. Vanaf nu vragen
            we bij elke login om een code uit je authenticator-app.
          </p>
        </div>
      </div>
    )
  }

  // phase === 'scan'
  return (
    <div className="flex flex-col gap-5">
      <ol className="flex flex-col gap-1.5 text-sm text-muted-foreground">
        <li>1. Open je authenticator-app (bijv. Google Authenticator of 1Password).</li>
        <li>2. Scan de QR-code hieronder, of voer de geheime sleutel handmatig in.</li>
        <li>3. Vul de 6-cijferige code uit de app in om te bevestigen.</li>
      </ol>

      {enrolment ? (
        <div className="flex flex-col items-center gap-3">
          <img
            src={enrolment.qrCode}
            alt="QR-code voor tweestapsverificatie"
            className="size-44 rounded-lg bg-white p-2 ring-1 ring-foreground/10"
          />
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs text-muted-foreground">
              Kan je de QR-code niet scannen? Voer deze sleutel handmatig in:
            </span>
            <code className="rounded-md bg-muted px-2 py-1 font-mono text-sm break-all select-all">
              {enrolment.secret}
            </code>
          </div>
        </div>
      ) : null}

      <form onSubmit={(event) => void handleVerify(event)} className="flex flex-col gap-4">
        <MfaCodeInput
          id="mfa-enrol-code"
          value={code}
          onChange={setCode}
          disabled={submitting || !enrolment}
          autoFocus
        />
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <Button
          type="submit"
          size="lg"
          disabled={submitting || !enrolment || code.length !== 6}
          className="w-full text-base"
        >
          {submitting ? 'Bezig met bevestigen…' : 'Bevestigen en inschakelen'}
        </Button>
      </form>
    </div>
  )
}
